import AutorenewIcon from '@mui/icons-material/Autorenew'
import { useState } from 'react'
import styled, { css } from 'styled-components'

import { Tooltip } from '@/components'
import { useAppContext } from '@/hooks'
import { useAdjustTableNameUsingPostMutation } from '@/rtkApi'
import { FormValue } from '@/types'
import { logError } from '@/utils/errorHandling'

import { FormFieldContext, withFormField } from '../UberForm/FormFieldContext'

interface InputCodeManualGenFormlessProps extends FormFieldContext {
	isUpperCase?: boolean,
	nameValue?: string,
	placeholder?: string
	showGenerateButton?: boolean
}

const InputCodeManualGenFormless = ({
	value,
	onChange,
	disabled,
	nameValue,
	isUpperCase = true,
	placeholder,
	name,
	showGenerateButton = true,
}: InputCodeManualGenFormlessProps) => {
	const { t } = useAppContext()
	const [adjustTableNameMutation] = useAdjustTableNameUsingPostMutation()
	const [isGenerating, setIsGenerating] = useState(false)

	const handleGenerateCode = async () => {
		if (!nameValue || disabled) return

		setIsGenerating(true)
		try {
			const response = await adjustTableNameMutation({
				utilsNameRequestDto: { name: nameValue },
			}).unwrap()

			const generatedCode = isUpperCase
				? response.result
				: response.result.toLowerCase()

			onChange?.(generatedCode)
		} catch (error) {
			logError(error, { operation: 'InputCodeManualGen.handleGenerateCode' })
		} finally {
			setIsGenerating(false)
		}
	}

	const handleChange = (formValue: FormValue) => {
		onChange?.(formValue)
	}

	const canShowButton = showGenerateButton && !disabled

	return (
		<Container disabled={disabled} data-testid={`code-input-container-${name}`}>
			<StyledInput
				type="text"
				name={name}
				value={(value as string) ?? ''}
				onChange={(e) => handleChange(e.target.value || undefined)}
				disabled={disabled}
				placeholder={placeholder}
				data-testid={`code-input-field-${name}`}
			/>
			{canShowButton && (
				<ButtonWrapper>
					<Tooltip content={t('GENERATE_CODE_FROM_NAME')}>
						<GenerateButton
							type="button"
							onClick={handleGenerateCode}
							disabled={!nameValue || isGenerating}
							data-testid={`code-input-button-generate-${name}`}
						>
							<AutorenewIcon
								fontSize="small"
								className={isGenerating ? 'animate-spin' : ''}
							/>
						</GenerateButton>
					</Tooltip>
				</ButtonWrapper>
			)}
		</Container>
	)
}

export const InputCodeManualGen = withFormField(InputCodeManualGenFormless)

const Container = styled.div<{ disabled?: boolean }>`
	width: 100%;
	display: flex;
	align-items: stretch;
	border: 1px solid ${(props) => props.theme.colors.input.border};
	border-radius: ${(props) => props.theme.input.borderRadius};
	background: ${(props) =>
		props.disabled ? props.theme.colors.input.disabled.background : 'white'};

	&:focus-within {
		border-color: ${(props) => props.theme.colors.primary.base};
		box-shadow: 0 0 3px ${(props) => props.theme.colors.primary.shadowColor};
	}

	&:hover {
		box-shadow: ${(props) =>
			props.disabled
				? 'none'
				: `0 0 3px ${props.theme.colors.primary.shadowColor}`};
	}
`

const StyledInput = styled.input`
	flex: 1 1 auto;
	min-width: 0;
	border: none;
	outline: none;
	padding: ${(props) => props.theme.input.padding};
	background: transparent;

	&:disabled {
		cursor: not-allowed;
	}
`

const ButtonWrapper = styled.div`
	display: flex;
	flex-shrink: 0;

	& > * {
		display: flex;
	}
`

const GenerateButton = styled.button<{ disabled?: boolean }>`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	border: none;
	background: transparent;
	cursor: pointer;
	transition: 0.2s;
	&:hover:not(:disabled) {
		background: ${(props) => props.theme.colors.primary.shadowColor};
	}

	${(props) =>
		props.disabled &&
		css`
			cursor: not-allowed;
			opacity: 0.5;
			background: ${props.theme.colors.input.disabled.background};
		`}
`

